<template>
  <div class="applicants-summary-page">
    <h1 class="global-h1 applicants-summary-page__title">Creator Applications for "{{ pageTitle }}"</h1>

    <ul v-if="!isLoading && summary" class="collab-details__snippets-list visit-details">
      <!-- Preferred Visit Window (copied from CollabInputsSummary) -->
      <li class="snippet collab-details__snippet">
        <IconEmbedded class="snippet__icon" name="calendar_2" :size="25" />
        <div class="snippet__content snippet__content--flex-column">
          <div class="snippet__text-wrap">
            <div class="global-h6 snippet__title">Preferred Visit Window</div>
            <div class="snippet__description">
              <span>{{ formatPreferredTimingDates }} </span><br />
              <span v-if="summary.preferredTiming?.length">
                {{ summary.preferredTiming }}
              </span>
            </div>
          </div>
        </div>
      </li>

      <!-- Visit Focus (copied from CollabInputsSummary) -->
      <li class="snippet collab-details__snippet">
        <IconEmbedded v-if="!summary.themeType" class="snippet__icon" name="bulb_2" :size="25" />
        <img v-else class="snippet__icon" :src="getThemeIcon(summary.themeType)" :alt="summary.themeType" />
        <div class="snippet__content">
          <div class="snippet__text-wrap">
            <div class="global-h6 snippet__title">
              {{ `Visit Focus: ${getThemeTypeName(summary.themeType)}` }}
            </div>
            <div class="snippet__description">
              <span v-if="summary.themes" v-html="linkifyStr(summary.themes, globalRoot.linkifyOptions)?.trim()"></span>
            </div>
          </div>
        </div>
      </li>
    </ul>

    <template v-if="!isLoading && isExpired">
      <h3 class="global-h2" style="margin-top: 50px">A creator has been selected for this visit!</h3>
    </template>

    <template v-else-if="!isLoading">
      <ul v-if="applications?.length" class="applicants-list">
        <PaidCreatorSummary
          v-for="creator in applications"
          :key="`applicant-${creator.creatorId}`"
          class="applicants-list__item"
          :creator="creator"
          :customerId="summary?.customerId ?? (route.params.communityId as string)"
          :collabInputId="summary?.collabInputId ?? (route.params.collabInputId as string)"
          :communityCanCoverLodging="summary?.communityCanCoverLodging === true"
          :showRanking="true"
          :isCTAButtonVisible="false"
          :isSaveCreatorLinkVisible="false"
          :allApplications="applications"
        />
      </ul>

      <div v-if="isButtonsVisible" class="buttons-section">
        <div class="divider" style="margin-bottom: 20px"></div>
        <h2>Share with your Stakeholders!</h2>
        <p>
          Copy the link and email to others (it works for users without Shrpa accounts).<br />
          Or print this page as a pdf (this section will be hidden).
        </p>
        <div style="gap: 20px; display: flex">
          <SrpButton @click="copyPageURLToClipboard" :isDisabled="isShowCopied">
            <template #icon>
              <IconEmbedded v-if="isShowCopied" name="check_3-5" :size="20" />
              <IconEmbedded v-else name="copy_2" :size="20" />
            </template>
            Copy Link
          </SrpButton>

          <SrpButton fill="outlined" @click="handlePrint">
            <template #icon>
              <IconEmbedded name="printer_2" :size="20" />
            </template>
            Print
          </SrpButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, onBeforeMount, nextTick, inject } from "vue";
import { AnonymousCreatorApplicantsSummaryResponse, CreatorCollabMatch } from "@contracts/collab";
import { useRoute } from "vue-router";
import CollabsRepo from "@repos/CollabsRepo";
import { RouteHelper } from "@helpers/RouteHelper";
import DateUtils from "@logic/DateUtils";
import linkifyStr from "linkify-string";
import { getThemeTypeName, getThemeIcon } from "@helpers/ThemeTypeHelper";

// Components
import PaidCreatorSummary from "@components/PaidCreatorSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Router
const route = useRoute();
const globalRoot = inject("globalRoot") as any;

// Load Anonymous Creator Applicants ==========================================
const isLoading = ref<boolean>(true);
const summary = ref<AnonymousCreatorApplicantsSummaryResponse | null>(null);

const isExpired = computed(() => summary.value?.isExpired === true);
const applications = computed<CreatorCollabMatch[]>(() => summary.value?.applications ?? []);
const pageTitle = computed(() => summary.value?.name ?? "");

const formatPreferredTimingDates = computed(() => {
  if (summary.value?.preferredDateRangeStart) {
    return DateUtils.formatDateRangeTwoDates(summary.value.preferredDateRangeStart, summary.value.preferredDateRangeEnd);
  }
  return "";
});

/** SrpMasonry only rebuilds on window resize / image load — nudge it after layout settles */
function rebuildFeaturedMediaMasonry() {
  window.dispatchEvent(new Event("resize"));
}

onMounted(async () => {
  window.addEventListener("beforeprint", prepareMessagesForPrint);
  window.addEventListener("afterprint", restoreMessagesAfterPrint);

  isLoading.value = true;

  summary.value = await CollabsRepo.loadAnonymousCreatorApplicantsSummary(route.params.communityId as string, route.params.collabInputId as string);

  isLoading.value = false;
  await nextTick();
  // Let flex column widths settle, then rebuild masonry (wrong width = overlapping images)
  requestAnimationFrame(() => {
    rebuildFeaturedMediaMasonry();
    setTimeout(rebuildFeaturedMediaMasonry, 350);
  });
});

onUnmounted(() => {
  window.removeEventListener("beforeprint", prepareMessagesForPrint);
  window.removeEventListener("afterprint", restoreMessagesAfterPrint);
});

// Hide buttons if the query present ==========================================
const isButtonsVisible = ref<boolean>(true);
const isButtonsVisibleQueryText = "share";

onBeforeMount(() => {
  const queryValue = RouteHelper.getQueryStringOrHashParam(isButtonsVisibleQueryText);

  if (queryValue) {
    isButtonsVisible.value = false;
  }
});

// Copy page URL ==============================================================
const isShowCopied = ref<boolean>(false);

async function copyPageURLToClipboard(): Promise<void> {
  const textToCopy = window.location.origin + window.location.pathname + `?${isButtonsVisibleQueryText}=true`;

  navigator.clipboard.writeText(textToCopy).then(
    () => {
      console.info("Copied to clipboard: " + textToCopy);
    },
    function (err) {
      console.error("Failed to copy to clipboard: ", err);
    }
  );

  isShowCopied.value = true;

  setTimeout(() => {
    isShowCopied.value = false;
  }, 3000);
}

// Handle print ===============================================================
function prepareMessagesForPrint() {
  // Force print layout before checking whether the 24-line clamp overflows.
  void document.body.offsetHeight;

  document.querySelectorAll<HTMLElement>(".applicants-summary-page .message-cloud").forEach(messageCloud => {
    const text = messageCloud.querySelector<HTMLElement>(".clamped-text-container");
    const showMore = messageCloud.querySelector<HTMLElement>(".link-with-icon");
    if (!text || !showMore) return;

    const isTruncated = text.scrollHeight > text.clientHeight + 1;
    showMore.style.setProperty("display", isTruncated ? "inline-flex" : "none", "important");
  });
}

function restoreMessagesAfterPrint() {
  document.querySelectorAll<HTMLElement>(".applicants-summary-page .message-cloud .link-with-icon").forEach(showMore => {
    showMore.style.removeProperty("display");
  });
}

function handlePrint() {
  // SrpMasonry rebuilds itself when print media becomes active
  window.print();
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

.applicants-summary-page {
  box-sizing: border-box;
  width: 100%;
  max-width: 1150px;
  margin: 0 auto;
  padding: 40px 25px 0;
}

.applicants-summary-page__title {
  margin-bottom: 30px;
}

.visit-details {
  margin-bottom: 30px !important;
  padding-bottom: 24px !important;
  padding-top: 18px !important;
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  border-top: 1px rgba(0, 0, 0, 0.1) solid;
}

// Snippet styles copied from CollabInputsSummary (not shared)
.snippet {
  padding-bottom: 9px;
  display: flex;
  justify-content: space-between;

  &:last-child {
    padding-bottom: 0;
  }

  &__icon {
    width: 27px;
    min-width: 27px;
    height: 27px;
    margin-right: 7px;
    display: flex;
    justify-content: center;
    align-items: center;

    :deep(svg) {
      fill: rgba(0, 0, 0, 0.3);
    }
  }

  &__content {
    flex-grow: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &--flex-column {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  &__text-wrap {
    flex-grow: 1;
    margin-right: 25px;
    display: flex;
    flex-direction: column;

    &:last-child {
      margin-right: 0;
    }
  }

  &__title {
    margin-bottom: 1px;
    font-weight: 600;
  }

  &__description {
    width: 100%;
    color: #5b5b5b;
    font: 14px / 18px sans-serif;
    white-space: pre-wrap;
    word-break: break-word;
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }

    :deep(a) {
      color: #2165a6;
      text-decoration: underline;
    }

    :deep(a:hover) {
      text-decoration: none;
    }
  }
}

.collab-details {
  &__snippets-list {
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__snippet {
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Match Creator Applications two-column PaidCreatorSummary layout
.applicants-list {
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  list-style: none;

  &__item {
    width: calc(50% - $desktop-wide-grid-gap-width / 2);
    margin: 0 $desktop-wide-grid-gap-width $desktop-wide-grid-gap-width 0;
    position: relative;
    z-index: 0;

    &:nth-child(2n) {
      margin-right: 0;
    }
  }
}

.buttons-section {
  margin-top: 40px;
  padding-bottom: 80px;
}

.divider {
  border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
}

// Masonry uses max-width: 100vw/columns which overflows these half-width cards
:deep(.srp-masonry__item) {
  max-width: 100% !important;
}

@media print {
  .applicants-summary-page {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .buttons-section {
    display: none !important;
  }

  // Force two columns — print page width otherwise matches tablet/mobile and collapses to 1
  .applicants-list {
    display: flex !important;
    flex-wrap: wrap !important;

    &__item {
      width: calc(50% - 13.5px) !important;
      margin: 0 27px 27px 0 !important;
      break-inside: avoid;
      page-break-inside: avoid;

      &:nth-child(2n) {
        margin-right: 0 !important;
      }
    }
  }

  // Keep the gray card background when printing
  :deep(.creator-snippet) {
    background: rgba(240, 240, 240, 1) !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  // Keep printed galleries compact (~30% shorter than on-screen minHeight), regardless of expanded state
  :deep(.featured-media__masonry-wrap) {
    height: 182px !important;
    max-height: 182px !important;
    padding-bottom: 0 !important;
    overflow: hidden !important;
  }

  :deep(.featured-media__show-more-btn) {
    display: none !important;
  }

  // Fade the cropped photos into the card background
  :deep(.featured-media::after) {
    display: block !important;
    height: 50px;
    opacity: 1 !important;
    background: linear-gradient(0deg, rgba(240, 240, 240, 1) 35%, transparent 100%);
  }

  // Hide creator profile "View" + external-link control
  :deep(.main-info__view-link) {
    display: none !important;
  }

  // Show more message text than on screen while keeping long cards on one page
  :deep(.message-cloud .clamped-text-container) {
    max-height: 400px !important;
    overflow: hidden !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 24 !important;
    line-clamp: 24 !important;
    -webkit-box-orient: vertical !important;
    text-overflow: ellipsis !important;
    mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 96%, rgba(0, 0, 0, 0) 100%) !important;
  }
}

@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .applicants-list__item {
    width: calc(50% - $desktop-grid-gap-width / 2);
    margin: 0 $desktop-grid-gap-width $desktop-grid-gap-width 0;
  }
}

@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .applicants-list__item {
    width: calc(50% - $laptop-grid-gap-width / 2);
    margin: 0 $laptop-grid-gap-width $laptop-grid-gap-width 0;
  }
}

@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .applicants-list__item {
    width: calc(50% - $tablet-large-grid-gap-width / 2);
    margin: 0 $tablet-large-grid-gap-width $tablet-large-grid-gap-width 0;
  }
}

@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .applicants-list__item {
    width: 100%;
    margin: 0 0 $tablet-grid-gap-width 0;
  }
}

@media (max-width: $mobile-max-width) {
  .applicants-summary-page {
    padding: 24px 15px 0;
  }

  .applicants-list__item {
    width: 100%;
    margin: 0 0 17px 0 !important;
  }
}
</style>
